import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { Task, Sprint } from '../page';
import { triggerChangeStepWebhook } from '@/hooks/useWebhook';

interface ClientBoardProps {
  tasks: Task[];
  sprints: Sprint[];
  project: { id: string; name: string; status: string; client_id: string | null; _client_name: string } | null;
  userId: string;
  userProfile: { id: string; full_name?: string; role?: string } | null;
  onRefresh?: () => void;
}

const COLUMNS = [
  { key: 'backlog', label: 'Backlog', color: 'bg-slate-100', dot: 'bg-slate-400', headerText: 'text-slate-600' },
  { key: 'todo', label: 'A Fazer', color: 'bg-cyan-50', dot: 'bg-cyan-500', headerText: 'text-cyan-700' },
  { key: 'doing', label: 'Fazendo', color: 'bg-amber-50', dot: 'bg-amber-400', headerText: 'text-amber-700' },
  { key: 'validation', label: 'Em Validação', color: 'bg-violet-50', dot: 'bg-violet-400', headerText: 'text-violet-700' },
  { key: 'done', label: 'Concluído', color: 'bg-emerald-50', dot: 'bg-emerald-500', headerText: 'text-emerald-700' },
];

const PRIORITY_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  low: { label: 'Baixa', color: 'text-slate-400', icon: 'ri-arrow-down-line' },
  medium: { label: 'Média', color: 'text-orange-400', icon: 'ri-arrow-right-line' },
  high: { label: 'Alta', color: 'text-red-500', icon: 'ri-arrow-up-line' },
  critical: { label: 'Crítica', color: 'text-red-700', icon: 'ri-arrow-up-double-line' },
};

type TaskWithPriority = Task & { client_priority?: number };

export default function ClientBoard({ tasks, sprints, project, userId, userProfile, onRefresh }: ClientBoardProps) {
  const activeSprint = sprints.find(s => s.status === 'active');
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  const fmt = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const getColumnTasks = (status: string): TaskWithPriority[] => {
    const col = tasks.filter(t => t.board_status === status) as TaskWithPriority[];
    if (status === 'todo') {
      return [...col].sort((a, b) => (a.client_priority ?? 999) - (b.client_priority ?? 999));
    }
    return col;
  };

  const buildWebhookBase = async () => {
    let clientEmail: string | null = null;
    if (project?.client_id) {
      const { data } = await supabase.from('clients').select('email').eq('id', project.client_id).maybeSingle();
      clientEmail = data?.email ?? null;
    }
    return {
      project: { id: project?.id ?? '', name: project?.name ?? '', status: project?.status ?? '', client_id: project?.client_id ?? null },
      client: { id: project?.client_id ?? null, name: project?._client_name ?? null, email: clientEmail },
      triggered_by: { user_id: userId, user_name: userProfile?.full_name ?? null, role: userProfile?.role ?? null },
    };
  };

  const moveToTodo = async (task: TaskWithPriority) => {
    setLoadingId(task.id);
    const todoTasks = getColumnTasks('todo');
    const maxPriority = todoTasks.length > 0
      ? Math.max(...todoTasks.map(t => t.client_priority ?? 0)) + 1
      : 1;

    const { error } = await supabase
      .from('tasks')
      .update({
        board_status: 'todo',
        client_priority: maxPriority,
        updated_at: new Date().toISOString(),
      })
      .eq('id', task.id);

    if (error) {
      console.error('Erro ao mover para A Fazer:', error);
      setLoadingId(null);
      return;
    }

    try {
      const base = await buildWebhookBase();
      await triggerChangeStepWebhook({
        event: 'change_step', action: 'moved_to_todo',
        task: { id: task.id, title: task.title, description: task.description, type: task.type, priority: task.priority, board_status: 'todo', client_priority: maxPriority, estimate_hours: task.estimate_hours, estimate_days: task.estimate_days, value: task.value, paid: task.paid, sprint_desired: task.sprint_desired, sprint_id: task.sprint_id, observations: task.observations, created_at: task.created_at },
        ...base, timestamp: new Date().toISOString(),
      });
    } catch (e) {
      console.error('Webhook error:', e);
    }

    setLoadingId(null);
    showSuccess(`"${task.title}" movida para A Fazer!`);
    onRefresh?.();
  };

  const moveToBacklog = async (task: TaskWithPriority) => {
    setLoadingId(task.id);

    const { error } = await supabase
      .from('tasks')
      .update({
        board_status: 'backlog',
        client_priority: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', task.id);

    if (error) {
      console.error('Erro ao mover para Backlog:', error);
      setLoadingId(null);
      return;
    }

    try {
      const base = await buildWebhookBase();
      await triggerChangeStepWebhook({
        event: 'change_step', action: 'moved_to_backlog',
        task: { id: task.id, title: task.title, description: task.description, type: task.type, priority: task.priority, board_status: 'backlog', client_priority: null, estimate_hours: task.estimate_hours, estimate_days: task.estimate_days, value: task.value, paid: task.paid, sprint_desired: task.sprint_desired, sprint_id: task.sprint_id, observations: task.observations, created_at: task.created_at },
        ...base, timestamp: new Date().toISOString(),
      });
    } catch (e) {
      console.error('Webhook error:', e);
    }

    setLoadingId(null);
    showSuccess(`"${task.title}" voltou para o Backlog!`);
    onRefresh?.();
  };

  const movePriority = async (todoTasks: TaskWithPriority[], taskId: string, direction: 'up' | 'down') => {
    const idx = todoTasks.findIndex(t => t.id === taskId);
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === todoTasks.length - 1) return;

    setLoadingId(taskId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    const taskA = todoTasks[idx];
    const taskB = todoTasks[swapIdx];
    const prioA = taskA.client_priority ?? idx + 1;
    const prioB = taskB.client_priority ?? swapIdx + 1;

    await Promise.all([
      supabase.from('tasks').update({ client_priority: prioB, updated_at: new Date().toISOString() }).eq('id', taskA.id),
      supabase.from('tasks').update({ client_priority: prioA, updated_at: new Date().toISOString() }).eq('id', taskB.id),
    ]);

    try {
      const base = await buildWebhookBase();
      await triggerChangeStepWebhook({
        event: 'change_step', action: 'priority_changed',
        task: { id: taskA.id, title: taskA.title, description: taskA.description, type: taskA.type, priority: taskA.priority, board_status: 'todo', client_priority: prioB, estimate_hours: taskA.estimate_hours, estimate_days: taskA.estimate_days, value: taskA.value, paid: taskA.paid, sprint_desired: taskA.sprint_desired, sprint_id: taskA.sprint_id, observations: taskA.observations, created_at: taskA.created_at },
        ...base, timestamp: new Date().toISOString(),
      });
    } catch (e) {
      console.error('Webhook error:', e);
    }

    setLoadingId(null);
    onRefresh?.();
  };

  return (
    <div>
      {/* Toast */}
      {successMsg && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-500 text-white px-5 py-3 rounded-xl text-sm font-medium flex items-center gap-2 shadow-lg animate-pulse">
          <i className="ri-checkbox-circle-line text-base"></i>
          {successMsg}
        </div>
      )}

      {/* Sprint ativa */}
      {activeSprint && (
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-5 py-3 mb-5 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100">
            <i className="ri-flashlight-line text-violet-600 text-sm"></i>
          </div>
          <div>
            <p className="text-violet-800 font-semibold text-sm">{activeSprint.name}</p>
            {activeSprint.goal && <p className="text-violet-600 text-xs mt-0.5">{activeSprint.goal}</p>}
            {activeSprint.start_date && activeSprint.end_date && (
              <p className="text-violet-500 text-xs mt-0.5">
                {new Date(activeSprint.start_date + 'T12:00:00').toLocaleDateString('pt-BR')} →{' '}
                {new Date(activeSprint.end_date + 'T12:00:00').toLocaleDateString('pt-BR')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Board */}
      <div className="flex gap-4 overflow-x-auto pb-4">
        {COLUMNS.map(col => {
          const colTasks = getColumnTasks(col.key);
          const isTodo = col.key === 'todo';
          const isBacklog = col.key === 'backlog';

          return (
            <div key={col.key} className="flex-shrink-0 w-64">
              {/* Header */}
              <div className={`flex items-center justify-between px-3 py-2 rounded-lg mb-3 ${col.color}`}>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${col.dot}`}></div>
                  <span className={`text-xs font-semibold ${col.headerText}`}>{col.label}</span>
                </div>
                <span className="text-slate-500 text-xs font-medium bg-white/60 px-1.5 py-0.5 rounded-full">{colTasks.length}</span>
              </div>

              {/* Legenda prioridade */}
              {isTodo && colTasks.length > 1 && (
                <div className="flex items-center gap-1 mb-2 px-1">
                  <i className="ri-sort-asc text-slate-300 text-xs"></i>
                  <span className="text-slate-400 text-xs">Use as setas para ordenar prioridade</span>
                </div>
              )}

              {/* Cards */}
              <div className="space-y-2">
                {colTasks.length === 0 && (
                  <div className="border-2 border-dashed border-slate-100 rounded-lg py-8 text-center">
                    <i className="ri-inbox-line text-slate-200 text-xl block mb-1"></i>
                    <p className="text-slate-300 text-xs">Vazio</p>
                  </div>
                )}

                {colTasks.map((task, idx) => {
                  const prio = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
                  const isLoading = loadingId === task.id;

                  return (
                    <div
                      key={task.id}
                      className={`bg-white rounded-lg border border-slate-200 p-3 transition-all ${isLoading ? 'opacity-60' : 'hover:border-slate-300'}`}
                    >
                      {/* Tipo + prioridade */}
                      <div className="flex items-center justify-between mb-2">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${
                          task.type === 'bug' ? 'bg-red-50 text-red-600' : 'bg-violet-50 text-violet-600'
                        }`}>
                          <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                          {task.type === 'bug' ? 'Bug' : 'Demanda'}
                        </span>
                        <span className={`flex items-center gap-0.5 text-xs ${prio.color}`}>
                          <i className={prio.icon}></i>
                          <span className="hidden">{prio.label}</span>
                        </span>
                      </div>

                      {/* Título */}
                      <p className="text-slate-700 text-xs font-medium leading-snug mb-3">{task.title}</p>

                      {/* ── Ações Backlog: botão mover para A Fazer ── */}
                      {isBacklog && (
                        <button
                          onClick={() => moveToTodo(task)}
                          disabled={isLoading}
                          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-cyan-500 hover:bg-cyan-600 text-white transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading
                            ? <i className="ri-loader-4-line animate-spin"></i>
                            : <i className="ri-arrow-right-circle-line"></i>
                          }
                          {isLoading ? 'Movendo...' : 'Mover para A Fazer'}
                        </button>
                      )}

                      {/* ── Ações A Fazer: ordenar + voltar ── */}
                      {isTodo && (
                        <div className="space-y-1.5">
                          {/* Badge de posição */}
                          <div className="flex items-center gap-1.5 mb-1">
                            <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                              #{idx + 1}
                            </span>
                            {idx === 0 && (
                              <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                                1ª prioridade
                              </span>
                            )}
                          </div>

                          {/* Setas de prioridade */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => movePriority(colTasks, task.id, 'up')}
                              disabled={idx === 0 || isLoading}
                              title="Aumentar prioridade (subir)"
                              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-arrow-up-s-line"></i>
                              Subir
                            </button>
                            <button
                              onClick={() => movePriority(colTasks, task.id, 'down')}
                              disabled={idx === colTasks.length - 1 || isLoading}
                              title="Diminuir prioridade (descer)"
                              className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-arrow-down-s-line"></i>
                              Descer
                            </button>
                          </div>

                          {/* Voltar ao backlog */}
                          <button
                            onClick={() => moveToBacklog(task)}
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs font-medium bg-slate-50 text-slate-500 hover:bg-red-50 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                          >
                            {isLoading
                              ? <i className="ri-loader-4-line animate-spin"></i>
                              : <i className="ri-arrow-left-circle-line"></i>
                            }
                            Voltar ao Backlog
                          </button>
                        </div>
                      )}

                      {/* Footer */}
                      <div className={`flex items-center justify-between text-xs text-slate-400 ${(isBacklog || isTodo) ? 'mt-2 pt-2 border-t border-slate-50' : ''}`}>
                        <div className="flex items-center gap-2">
                          {task.estimate_hours > 0 && <span><i className="ri-time-line mr-0.5"></i>{task.estimate_hours}h</span>}
                          {task.estimate_days > 0 && <span><i className="ri-calendar-line mr-0.5"></i>{task.estimate_days}d</span>}
                        </div>
                        <div className="flex items-center gap-1">
                          {task.value > 0 && (
                            <span className={task.paid ? 'text-emerald-500' : 'text-amber-500'}>{fmt(task.value)}</span>
                          )}
                          {task.paid && <i className="ri-checkbox-circle-fill text-emerald-500"></i>}
                          {task.sprint_desired && <i className="ri-star-fill text-amber-400"></i>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
